import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import moment from 'moment';
import { ROOT_URL } from '../../api/ApiConfig';

const emptyStatsData = {
    draft: 0,
    pendingReview: 0,
    published: 0,
    archived: 0,
    total: 0,
};

export const getKbOverviewStats = createAsyncThunk(
    'kbOverview/getKbOverviewStats',
    async ({ business_id, group_id }, { getState, rejectWithValue }) => {
        try {
            const { loginToken } = getState();
            const authToken = loginToken?.response?.token;

            if (!business_id) {
                throw new Error('Business ID not found');
            }

            const params = new URLSearchParams({ business_id });
            if (group_id) {
                params.append('group_id', group_id);
            }

            const url = `${ROOT_URL}/kb/articles/stats?${params.toString()}`;

            const response = await axios.get(url, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching KB stats:', error);
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch KB stats'
            );
        }
    }
);

export const getKbRecentActivity = createAsyncThunk(
    'kbOverview/getKbRecentActivity',
    async ({ business_id, group_id }, { getState, rejectWithValue }) => {
        try {
            const { loginToken } = getState();
            const authToken = loginToken?.response?.token;

            if (!business_id) {
                throw new Error('Business ID not found');
            }

            const params = new URLSearchParams({ business_id });
            if (group_id) {
                params.append('group_id', group_id);
            }

            const url = `${ROOT_URL}/kb/articles/recent?${params.toString()}`;

            const response = await axios.get(url, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching recent KB activity:', error);
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch recent activity'
            );
        }
    }
);

const initialState = {
    statsLoading: true,
    recentLoading: true,
    statsError: null,
    recentError: null,
    /** After first successful load, refetches skip skeleton-style loading states. */
    statsHydrated: false,
    recentHydrated: false,
    stats: {
        data: { ...emptyStatsData }
    },
    /** Group-scoped tab counts for Articles / Review Queue (does not overwrite overview stats). */
    groupStats: {
        data: { ...emptyStatsData }
    },
    groupStatsGroupId: null,
    groupStatsLoading: false,
    recentGroupId: null,
    recentActivity: []
};

const kbOverviewSlice = createSlice({
    name: 'kbOverview',
    initialState,
    reducers: {
        resetKbOverview: () => initialState,
        clearError: (state) => {
            state.statsError = null;
            state.recentError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Stats — group-scoped (Overview + Articles/Review tab counts)
            .addCase(getKbOverviewStats.pending, (state, action) => {
                const groupId = action.meta?.arg?.group_id ?? null;
                state.statsError = null;
                if (groupId) {
                    const sameGroup =
                        state.groupStatsGroupId != null &&
                        String(state.groupStatsGroupId) === String(groupId);
                    if (!sameGroup) {
                        state.groupStats = { data: { ...emptyStatsData } };
                        state.stats = { data: { ...emptyStatsData } };
                        state.groupStatsGroupId = groupId;
                    }
                    state.groupStatsLoading = !sameGroup;
                    state.statsLoading = !sameGroup;
                } else {
                    state.statsLoading = !state.statsHydrated;
                }
            })
            .addCase(getKbOverviewStats.fulfilled, (state, action) => {
                const groupId = action.meta?.arg?.group_id ?? null;
                state.statsError = null;
                if (groupId) {
                    state.groupStatsLoading = false;
                    state.statsLoading = false;
                    state.groupStats = action.payload;
                    state.stats = action.payload;
                    state.groupStatsGroupId = groupId;
                    state.statsHydrated = true;
                } else {
                    state.statsLoading = false;
                    state.stats = action.payload;
                    state.statsHydrated = true;
                }
            })
            .addCase(getKbOverviewStats.rejected, (state, action) => {
                const groupId = action.meta?.arg?.group_id ?? null;
                state.statsError = action.payload;
                if (groupId) {
                    state.groupStatsLoading = false;
                    state.statsLoading = false;
                    state.groupStats = { data: { ...emptyStatsData } };
                    state.stats = { data: { ...emptyStatsData } };
                    state.groupStatsGroupId = groupId;
                } else {
                    state.statsLoading = false;
                }
            })
            // Recent Activity (group-scoped)
            .addCase(getKbRecentActivity.pending, (state, action) => {
                const groupId = action.meta?.arg?.group_id ?? null;
                state.recentError = null;
                const sameGroup =
                    groupId != null &&
                    state.recentGroupId != null &&
                    String(state.recentGroupId) === String(groupId);
                if (groupId && !sameGroup) {
                    state.recentActivity = [];
                    state.recentGroupId = groupId;
                    state.recentLoading = true;
                } else {
                    state.recentLoading = !state.recentHydrated;
                }
            })
            .addCase(getKbRecentActivity.fulfilled, (state, action) => {
                const groupId = action.meta?.arg?.group_id ?? null;
                state.recentLoading = false;
                state.recentError = null;
                state.recentHydrated = true;
                if (groupId) state.recentGroupId = groupId;
                const rawData = action.payload.data || [];
                state.recentActivity = rawData.map(item => ({
                    id: item._id,
                    title: item.title,
                    status: item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1).toLowerCase() : '',
                    creator: item.authorId?.full_name || item.authorId?.email || 'Unknown',
                    updated: item.updatedAt ? moment(item.updatedAt).fromNow() : 'Unknown',
                    visibility: item.visibility,
                    isNew: moment(item.createdAt).isAfter(moment().subtract(24, 'hours'))
                }));
            })
            .addCase(getKbRecentActivity.rejected, (state, action) => {
                state.recentLoading = false;
                state.recentError = action.payload;
            });
    }
});

export const { resetKbOverview, clearError } = kbOverviewSlice.actions;

export default kbOverviewSlice.reducer;
